/**
 * Realtime Search Component
 * Uses Shopify's native search to find products
 */
class RealtimeSearch {
  constructor() {
    this.searchInput = null;
    this.resultsContainer = null;
    this.debounceTimer = null;
    
    this.init();
  }

  init() {
    this.searchInput = document.querySelector('.hero-search-input');
    this.resultsContainer = document.getElementById('search-results');
    
    if (!this.searchInput) return;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Real-time search on input
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);
      const query = e.target.value.trim();
      
      if (query.length === 0) {
        this.hideResults();
        return;
      }
      
      if (query.length >= 2) {
        this.debounceTimer = setTimeout(() => {
          this.performSearch(query);
        }, 300);
      }
    });

    // Handle native search clear (type="search" clear button)
    this.searchInput.addEventListener('search', () => {
      if (!this.searchInput.value.trim()) {
        this.hideResults();
      }
    });

    // Handle form submission
    const form = this.searchInput.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(this.debounceTimer);
        const query = this.searchInput.value.trim();
        if (query) {
          this.performSearch(query);
        }
      });
    }
  }

  performSearch(query) {
    if (!query) {
      this.hideResults();
      return;
    }

    // Use Shopify's native search with cache-busting
    fetch(`/search?q=${encodeURIComponent(query)}&type=product&_=${Date.now()}`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = this.extractProductsFromHTML(doc);
        this.displayResults(results.slice(0, 12), query);
      })
      .catch(error => {
        console.error('Search error:', error);
        this.displayResults([], query);
      });
  }

  extractProductsFromHTML(doc) {
    const products = [];
    
    const selectors = [
      '.exchange-product-card',
      '.product-item',
      '.product-card',
      '[data-product-id]',
      '.card-product'
    ];
    
    let productElements = [];
    for (const selector of selectors) {
      productElements = doc.querySelectorAll(selector);
      if (productElements.length > 0) break;
    }
    
    productElements.forEach(el => {
      const titleEl = el.querySelector('.product-title, .card-title, h3, h2, .title');
      const imageEl = el.querySelector('img[data-feauredImage], img');
      const descEl = el.querySelector('.card-product-desc, .product-description, .card-text, .description');
      const linkEl = el.querySelector('a') || el;
      const vendorEl = el.querySelector('.card-product-vendor, .product-vendor');
      const certifiedEl = el.querySelector('.certified-pill-badge');
      const cardDataEl = el.querySelector('[data-certified]');
      
      // Extract product type from triangle badge or data attribute
      let productType = '';
      const triangleApp = el.querySelector('.triangle-app');
      const triangleSolution = el.querySelector('.triangle-solution');
      const cardEl = el.querySelector('.card-product') || el;
      
      if (triangleApp) {
        productType = 'app';
      } else if (triangleSolution) {
        productType = 'solution';
      } else if (cardEl.dataset && cardEl.dataset.appType) {
        // Check data attribute
        const appType = cardEl.dataset.appType.toLowerCase();
        if (appType === 'app') productType = 'app';
        else if (appType === 'solution') productType = 'solution';
      }
      
      if (titleEl && titleEl.textContent.trim()) {
        const title = titleEl.textContent.trim();
        let description = '';
        if (descEl && !descEl.classList.contains('product-vendor')) {
          const descP = descEl.querySelector('p');
          description = descP ? descP.textContent.trim() : descEl.textContent.trim();
        }
        const vendorP = vendorEl ? vendorEl.querySelector('p') : null;
        const vendorText = vendorP ? vendorP.textContent.trim() : (vendorEl ? vendorEl.textContent.trim() : '');
        const vendor = vendorText.replace(/^By\s+/i, '').trim();
        
        products.push({
          title: title,
          image: imageEl ? imageEl.src : '',
          description: description.length > 100 ? description.substring(0, 100) + '...' : description,
          url: linkEl.href || '#',
          vendor: vendor,
          productType: productType,
          certified: certifiedEl !== null || (cardDataEl && cardDataEl.dataset.certified === 'true')
        });
      }
    });
    
    return products;
  }

  displayResults(results, query) {
    if (results.length === 0) {
      this.showNoResults(query);
      return;
    }

    this.showResults();
    
    const searchGrid = document.getElementById('search-grid');
    const searchCount = document.getElementById('search-count');
    
    if (searchCount) {
      searchCount.textContent = `${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`;
    }
    
    if (searchGrid) {
      searchGrid.innerHTML = results.map((product, index) => 
        this.createProductCard(product, index, query)
      ).join('');
    }
  }

  createProductCard(product, index, query = '') {
    const imageHtml = product.image 
      ? `<img srcset="${this.getOptimizedImageUrl(product.image, 300)} 300w, ${this.getOptimizedImageUrl(product.image, 533)} 533w"
             src="${this.getOptimizedImageUrl(product.image, 300)}"
             sizes="(min-width: 1200px) 300px, (min-width: 768px) calc((100vw - 130px) / 2), 50vw"
             loading="${index > 2 ? 'lazy' : 'eager'}"
             alt="${product.title}"
             width="300" height="200">`
      : `<svg class="placeholder-svg" width="300" height="200" viewBox="0 0 300 200" fill="none">
           <rect width="300" height="200" fill="#f8f9fa"/>
           <text x="150" y="100" text-anchor="middle" fill="#6c757d" font-size="14">No Image</text>
         </svg>`;

    // Determine triangle indicator based on product type
    let triangleHtml = '';
    if (product.productType === 'app') {
      triangleHtml = '<div class="product-type-indicator"><span class="triangle-badge triangle-app"><span>APP</span></span></div>';
    } else if (product.productType === 'solution') {
      triangleHtml = '<div class="product-type-indicator"><span class="triangle-badge triangle-solution"><span>SOLUTION</span></span></div>';
    }

    // Highlight search terms in title, vendor, and description
    const highlightedTitle = this.highlightSearchTerms(product.title, query);
    const highlightedVendor = this.highlightSearchTerms(product.vendor, query);
    const highlightedDescription = this.highlightSearchTerms(
      this.truncateText(product.description, 100), 
      query
    );

    return `
      <div class="exchange-product-card">
        <div class="card card-product text-start" data-product-grid>
          ${triangleHtml}
          <div class="product-image">
            <a href="${product.url}" class="d-block product-link position-relative">
              ${imageHtml}
            </a>
          </div>
          <div class="card-body">
            <h2 class="card-product-title">${highlightedTitle}</h2>
            <div class="card-product-badge">
              ${product.certified ? '<span class="certified-pill-badge">Saviynt Certified</span>' : ''}
            </div>
            <div class="card-product-vendor">
              ${product.vendor ? `<p>By ${highlightedVendor}</p>` : ''}
            </div>
            <div class="card-product-desc">
              ${product.description ? `<p>${highlightedDescription}</p>` : ''}
            </div>
            <div class="card-product-actions">
              <a href="${product.url}" class="btn-view-details">View Details</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  showResults() {
    // Hide all homepage content sections between hero and footer
    // Use CSS class selectors from section schemas (stable across environments)
    const sectionsToHide = [
      '.section-securing-identities',
      '.section-apps-solutions-cards',
      '.section-homepage-all'
    ];
    sectionsToHide.forEach(selector => {
      const section = document.querySelector(selector);
      if (section) section.style.display = 'none';
    });

    // Show search results
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'block';
      this.resultsContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  showNoResults(query) {
    this.showResults();
    
    const searchGrid = document.getElementById('search-grid');
    const searchCount = document.getElementById('search-count');
    const noResults = document.getElementById('no-results');
    
    if (searchCount) {
      searchCount.textContent = `No results for "${query}"`;
    }
    
    if (searchGrid) searchGrid.style.display = 'none';
    if (noResults) noResults.style.display = 'block';
  }

  hideResults() {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
    }

    // Restore all homepage content sections
    const sectionsToShow = [
      '.section-securing-identities',
      '.section-apps-solutions-cards',
      '.section-homepage-all'
    ];
    sectionsToShow.forEach(selector => {
      const section = document.querySelector(selector);
      if (section) section.style.display = '';
    });
  }

  getOptimizedImageUrl(url, width) {
    if (!url) return '';
    
    // Shopify image optimization - handle both formats
    const baseUrl = url.split('?')[0];
    if (baseUrl.includes('_') && baseUrl.match(/_\d+x\./)) {
      // Already has size, replace it
      return baseUrl.replace(/_\d+x\./i, `_${width}x.`);
    } else {
      // Add size before extension
      return baseUrl.replace(/\.(jpg|jpeg|png|webp)$/i, `_${width}x.$1`);
    }
  }

  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  addLoadingState() {
    if (this.searchInput) {
      this.searchInput.classList.add('search-loading');
    }
  }

  removeLoadingState() {
    if (this.searchInput) {
      this.searchInput.classList.remove('search-loading');
    }
  }

  highlightSearchTerms(text, query) {
    if (!query || !text) return text;
    
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
    let highlightedText = text;
    
    searchTerms.forEach(term => {
      const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<span class="search-highlight">$1</span>');
    });
    
    return highlightedText;
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RealtimeSearch();
});